import React, { useState, useMemo } from 'react'
import {
  DishesSectionContainer,
  DishesSectionHeader,
  DishesSectionTitle,
  SearchContainer,
  SearchInput,
  DishListContainer,
  DishCard,
  DishName,
  DishCategory,
  DishPrice,
  DishDescription,
  EmptyStateContainer,
  EmptyStateText,
} from '../styled/SavedMenuDetail.styled'
import ImpressionTracker from '../../../../components/analytics/ImpressionTracker'
import { logEvent } from '../../../../analytics/analytics'

export interface Dish {
  id: string
  name: string
  description?: string
  price?: string | number
  section?: string | null
  imageUrl?: string
}

interface SavedMenuDishListProps {
  dishes: Dish[]
  restaurantId?: string
  menuId?: string
}

const SavedMenuDishList: React.FC<SavedMenuDishListProps> = ({
  dishes,
  restaurantId,
  menuId,
}): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState('')

  // Filter dishes based on search
  const filteredDishes = useMemo(() => {
    return dishes.filter((dish) => {
      const matchesSearch =
        searchQuery === '' ||
        dish.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (dish.description &&
          dish.description.toLowerCase().includes(searchQuery.toLowerCase()))
      return matchesSearch
    })
  }, [dishes, searchQuery])

  const formatPrice = (price?: string | number): string => {
    if (!price) return ''
    if (typeof price === 'string') {
      // Global product: no currency symbol — strip any leading $ that may be in the data.
      return price.startsWith('$') ? price.slice(1) : price
    }
    return `${price}`
  }

  return (
    <DishesSectionContainer>
      {/* Section header */}
      <DishesSectionHeader>
        <DishesSectionTitle>
          Dishes ({dishes.length})
        </DishesSectionTitle>
      </DishesSectionHeader>

      {/* Search */}
      <SearchContainer>
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      {/* Dishes list or empty state */}
      {filteredDishes.length > 0 ? (
        <DishListContainer>
          {filteredDishes.map((dish, index) => (
            <ImpressionTracker
              key={dish.id}
              onImpress={() =>
                logEvent('dish_impression', {
                  restaurant_id: restaurantId ?? '',
                  dish_id: dish.id,
                  rank_position: index,
                  screen_name: 'saved_menu_detail',
                  menu_id: menuId,
                })
              }
            >
              <DishCard>
                <DishName>{dish.name}</DishName>
                {dish.section && <DishCategory>{dish.section}</DishCategory>}
                {dish.price && <DishPrice>{formatPrice(dish.price)}</DishPrice>}
                {dish.description && (
                  <DishDescription>{dish.description}</DishDescription>
                )}
              </DishCard>
            </ImpressionTracker>
          ))}
        </DishListContainer>
      ) : (
        <EmptyStateContainer>
          <EmptyStateText>
            {searchQuery
              ? 'No dishes match your search'
              : 'No dishes saved for this menu'}
          </EmptyStateText>
        </EmptyStateContainer>
      )}
    </DishesSectionContainer>
  )
}

export default SavedMenuDishList
